"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  Phone,
  Instagram,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ChevronDown,
  ChevronUp,
  Download,
  Smartphone,
  X,
  Menu,
  Calendar,
  MessageCircle,
} from "lucide-react";
import "./landingpage.css";
import { requestPermissionAndGetToken } from "firebase-helper";
import { AiFillInstagram } from "react-icons/ai";
import { CgInstagram } from "react-icons/cg";

// ─── Business Configuration ────────────────────────────────────────────────
const businessInfo = {
  name: "INK VOID",
  fullName: "INK VOID Tattoo Studio",
  slogan: "Deriye İşlenen Hikayeler",
  phone: "05551234567",
  phoneFormatted: "0555 123 45 67",
  whatsapp: "905551234567",
  instagram: "inkvoid_tattoo",
  address: "Kadıköy, Moda Cd. 47/B Kadıköy/İstanbul",
  domain: "inkvoid.com",
  appUrl: "https://inkvoid.com/auth/sign-in",
  mapEmbed:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.6634895060255!2d29.02680631541613!3d40.98884097930476!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab7a0e835d6b5%3A0x6b9b9b9b9b9b9b9b!2sKad%C4%B1k%C3%B6y%2C%20%C4%B0stanbul!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str",
};

// ─── Tattoo Styles (Services) ───────────────────────────────────────────────
const tattooStyles = [
  {
    id: 1,
    name: "REALISM",
    sub: "Fotoğrafik Gerçekçilik",
    image: "/images/dvm/dvm_real.jpg",
  },
  {
    id: 2,
    name: "TRADITIONAL",
    sub: "Klasik Amerikan Tarzı",
    image: "/images/dvm/dvm_trd.jpg",
  },
  {
    id: 3,
    name: "MINIMALIST",
    sub: "Sade ve Güçlü",
    image: "/images/dvm/dvm_mini.jpg",
  },
  {
    id: 4,
    name: "BLACKWORK",
    sub: "Saf Siyah Mürekkep",
    image: "/images/dvm/dvm_blckwrk.jpg",
  },
  {
    id: 5,
    name: "COVER-UP",
    sub: "Yeniden Doğuş",
    image: "/images/dvm/dvm_cvr.jpg",
  },
  {
    id: 6,
    name: "GEOMETRIC",
    sub: "Matematiksel Güzellik",
    image: "/images/dvm/dvm_geo.jpg",
  },
];

// ─── Artists ────────────────────────────────────────────────────────────────
const artists = [
  {
    id: 1,
    name: "Artist İsmi - Görseli",
    specialty: "Minimalist & Blackwork",
    exp: "8 Yıl Deneyim",
    img: "/images/artist_1.jpeg",
    ig: "kadir.ink",
  },
  {
    id: 2,
    name: "Artist İsmi - Görseli",
    specialty: "Realism & Portrait",
    exp: "6 Yıl Deneyim",
    img: "/images/artist_2.jpeg",
    ig: "sena.tattoo",
  },
  {
    id: 3,
    name: "Artist İsmi - Görseli",
    specialty: "Traditional & Neo-Trad",
    exp: "10 Yıl Deneyim",
    img: "/images/artist_3.jpeg",
    ig: "mert.void",
  },
  {
    id: 4,
    name: "Artist İsmi - Görseli",
    specialty: "Geometric & Ornamental",
    exp: "5 Yıl Deneyim",
    img: "/images/artist_4.jpeg",
    ig: "zelis.geometric",
  },
];

// ─── Gallery Items ──────────────────────────────────────────────────────────
const galleryItems = [
  { id: 1, type: "image", url: "/images/dvm/dvm_real.jpg", alt: "Realism" },
  { id: 2, type: "image", url: "/images/dvm/dvm_blckwrk.jpg", alt: "Blackwork" },
  { id: 3, type: "image", url: "/images/dvm/dvm_mini.jpg", alt: "Minimalist" },
  { id: 4, type: "image", url: "/images/dvm/dvm_trd.jpg", alt: "Traditional" },
  { id: 5, type: "image", url: "/images/dvm/dvm_cvr.jpg", alt: "Cover-Up" },
  { id: 6, type: "video", url: "/images/dvm/bg_vid_rev.mp4", alt: "Studio" },

];

// ─── Main Component ─────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [hoveredStyle, setHoveredStyle] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installModalVisible, setInstallModalVisible] = useState(false);

  const gallerySliderRef = useRef(null);
  const circleRef = useRef(null);
  const videoRef = useRef(null);

  // ── PWA Install Prompt ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    setInstallModalVisible(true);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => setDeferredPrompt(null));
    }
  };

  // ── Resize ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Video Load ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (videoRef.current && videoRef.current.readyState >= 3) {
      setIsVideoLoaded(true);
    }
    const safetyTimer = setTimeout(() => setIsVideoLoaded(true), 2000);
    return () => clearTimeout(safetyTimer);
  }, []);

  const handleVideoLoad = () => {
    setTimeout(() => setIsVideoLoaded(true), 600);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // ── Scroll Tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "services",
        "artists",
        "gallery",
        "about",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;
      setShowScrollTop(window.scrollY > 300);

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollableHeight = documentHeight - windowHeight;
      if (scrollableHeight > 0 && circleRef.current) {
        const scrollPercent = scrollTop / scrollableHeight;
        const circumference = 2 * Math.PI * 26;
        circleRef.current.style.strokeDashoffset =
          circumference - scrollPercent * circumference;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleBookAppointment = () => {
    navigate("/auth/sign-in");
  };

  const handleWhatsapp = () => {
    window.open(`https://wa.me/${businessInfo.whatsapp}`, "_blank");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
      setMenuOpen(false);
    }
  };

  const scrollToNext = () => {
    const el = document.getElementById("services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollGallery = (dir) => {
    if (!gallerySliderRef.current) return;
    const amount = window.innerWidth > 768 ? 500 : window.innerWidth * 0.88;
    gallerySliderRef.current.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const navLinks = [
    { id: "home", text: "Ana Sayfa" },
    { id: "services", text: "Stiller" },
    { id: "artists", text: "Sanatçılar" },
    { id: "gallery", text: "Portföy" },
    { id: "about", text: "Hakkımızda" },
    { id: "contact", text: "İletişim" },
  ];

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="iv-landing">
      {/* ── Loader ── */}
      <div className={`iv-loader ${isVideoLoaded ? "loaded" : ""}`}>
        <div className="iv-loader-ring" />
        <span className="iv-loader-brand">INK VOID</span>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                HAMBURGER BUTTON — Fixed corner
            ══════════════════════════════════════════════════════════════════ */}
      <button
        className={`iv-hamburger ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Menü"
      >
        <span className="iv-ham-line" />
        <span className="iv-ham-line" />
        <span className="iv-ham-line" />
      </button>

      {/* ══════════════════════════════════════════════════════════════════
                FULLSCREEN OVERLAY MENU
            ══════════════════════════════════════════════════════════════════ */}
      <div className={`iv-overlay-menu ${menuOpen ? "open" : ""}`}>
        <div className="iv-overlay-inner">
          <div className="iv-overlay-brand">INK VOID</div>
          <nav className="iv-overlay-nav">
            {navLinks.map((link, i) => (
              <button
                key={link.id}
                className={`iv-overlay-link ${
                  activeSection === link.id ? "active" : ""
                }`}
                onClick={() => scrollToSection(link.id)}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <span className="iv-overlay-num">0{i + 1}</span>
                {link.text}
              </button>
            ))}
          </nav>
          <div className="iv-overlay-bottom">
            <button className="iv-overlay-cta" onClick={handleBookAppointment}>
              <Calendar size={16} />
              Randevu Al
            </button>
            <div className="iv-overlay-socials">
              <a
                href={`https://www.instagram.com/${businessInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <CgInstagram size={20} />
              </a>
              <a
                href={`https://wa.me/${businessInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                HERO SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="home" className="iv-hero">
        <div className="iv-hero-video-wrap">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="iv-hero-video"
            onLoadedData={handleVideoLoad}
          >
            <source src="/images/dvm/bg_vid_rev.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="iv-hero-overlay" />

        <div className={`iv-hero-content ${!isMuted ? "transparent" : ""}`}>
          <div className="iv-hero-tag">TATTOO STUDIO · İSTANBUL</div>
          <h1 className="iv-hero-title">
            DERİNE
            <br />
            <span className="iv-hero-accent">İŞLENEN</span>
            <br />
            SANAT
          </h1>
          <p className="iv-hero-slogan">{businessInfo.slogan}</p>
          <button className="iv-hero-btn" onClick={handleBookAppointment}>
            <Calendar size={16} />
            RANDEVU AL
          </button>
        </div>



        <div className="iv-scroll-hint" onClick={scrollToNext}>
          <ChevronDown size={24} />
          <span>KAYDIr</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                STATS STRIP
            ══════════════════════════════════════════════════════════════════ */}
      <section className="iv-stats">
        <div className="iv-stats-inner">
          <div className="iv-stat-item">
            <span className="iv-stat-num">1200+</span>
            <span className="iv-stat-label">Tamamlanan Dövme</span>
          </div>
          <div className="iv-stat-divider" />
          <div className="iv-stat-item">
            <span className="iv-stat-num">4</span>
            <span className="iv-stat-label">Uzman Sanatçı</span>
          </div>
          <div className="iv-stat-divider" />
          <div className="iv-stat-item">
            <span className="iv-stat-num">8+</span>
            <span className="iv-stat-label">Yıl Deneyim</span>
          </div>
          <div className="iv-stat-divider" />
          <div className="iv-stat-item">
            <span className="iv-stat-num">%100</span>
            <span className="iv-stat-label">Steril Ortam</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                SERVICES — Typography Hover Effect
            ══════════════════════════════════════════════════════════════════ */}
      <section id="services" className="iv-services-section">
        <div
          className="iv-services-bg-layer"
          style={{
            backgroundImage: hoveredStyle
              ? `url(${hoveredStyle.image})`
              : "none",
            opacity: hoveredStyle ? 1 : 0,
          }}
        />
        <div
          className="iv-services-bg-dim"
          style={{ opacity: hoveredStyle ? 1 : 0 }}
        />

        <div className="iv-section-header">
          <span className="iv-section-tag">UZMANLIK ALANLARIMIZ</span>
          <h2 className="iv-section-title">Stilinizi Seçin</h2>
        </div>

        <div className="iv-styles-list">
          {tattooStyles.map((style) => (
            <div
              key={style.id}
              className={`iv-style-item ${
                hoveredStyle?.id === style.id ? "hovered" : ""
              }`}
              onMouseEnter={() => setHoveredStyle(style)}
              onMouseLeave={() => setHoveredStyle(null)}
              onTouchStart={() =>
                setHoveredStyle(hoveredStyle?.id === style.id ? null : style)
              }
            >
              <span className="iv-style-num">0{style.id}</span>
              <span className="iv-style-name">{style.name}</span>
              <span className="iv-style-sub">{style.sub}</span>
              <span className="iv-style-arrow">→</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                ARTISTS SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="artists" className="iv-artists-section">
        <div className="iv-section-header">
          <span className="iv-section-tag">EKİBİMİZ</span>
          <h2 className="iv-section-title">ARTISTS</h2>
          <p className="iv-section-sub">Her biri kendi tarzının ustası</p>
        </div>

        <div className="iv-artists-grid">
          {artists.map((artist) => (
            <div key={artist.id} className="iv-artist-card">
              <div className="iv-artist-img-wrap">
                <img
                  src={artist.img}
                  alt={artist.name}
                  className="iv-artist-img"
                  loading="lazy"
                />
                <div className="iv-artist-img-overlay" />
              </div>
              <div className="iv-artist-info">
                <h3 className="iv-artist-name">{artist.name}</h3>
                <span className="iv-artist-specialty">{artist.specialty}</span>
                <span className="iv-artist-exp">{artist.exp}</span>
                <a
                  href={`https://www.instagram.com/${artist.ig}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="iv-artist-ig"
                >
                  <CgInstagram size={14} />@{artist.ig}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                GALLERY — Horizontal Scroll, Grayscale→Color on Hover
            ══════════════════════════════════════════════════════════════════ */}
      <section id="gallery" className="iv-gallery-section">
        <div className="iv-section-header">
          <span className="iv-section-tag">PORTFÖY</span>
          <h2 className="iv-section-title">Mürekkep & Deri</h2>
        </div>

        <div className="iv-gallery-wrap">
          <button
            className="iv-gallery-btn left"
            onClick={() => scrollGallery("left")}
            aria-label="Önceki"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="iv-gallery-track" ref={gallerySliderRef}>
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="iv-gallery-item"
                onClick={() => setSelectedImage(item)}
              >
                {item.type === "video" ? (
                  <video
                    src={item.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="iv-gallery-media"
                  />
                ) : (
                  <img
                    src={item.url}
                    alt={item.alt || "INK VOID Tattoo"}
                    loading="lazy"
                    className="iv-gallery-media"
                  />
                )}
                <div className="iv-gallery-item-overlay">
                  <span className="iv-gallery-item-label">{item.alt}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            className="iv-gallery-btn right"
            onClick={() => scrollGallery("right")}
            aria-label="Sonraki"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div className="iv-lightbox" onClick={() => setSelectedImage(null)}>
            <button
              className="iv-lightbox-close"
              onClick={() => setSelectedImage(null)}
            >
              <X size={26} />
            </button>
            <div
              className="iv-lightbox-inner"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            >
              {selectedImage.type === "video" ? (
                <video
                  src={selectedImage.url}
                  controls
                  autoPlay
                  playsInline
                  className="iv-lightbox-media"
                />
              ) : (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="iv-lightbox-media"
                  draggable="false"
                />
              )}
              {selectedImage.alt && (
                <p className="iv-lightbox-caption">{selectedImage.alt}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                MOTTO / QUOTE SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section className="iv-motto-section">
        <div className="iv-motto-inner">
          <span className="iv-motto-quote">"</span>
          <p className="iv-motto-text">
            Her dövme bir karar değil, bir itiraftır. Derine işlenir, sonsuza
            taşınır.
          </p>
          <span className="iv-motto-author">— INK VOID Tattoo Studio</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                ABOUT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="about" className="iv-about-section">
        <div className="iv-about-grid">
          <div className="iv-about-content">
            <span className="iv-section-tag">HAKKIMIZDA</span>
            <h2 className="iv-section-title left">Hikayemiz</h2>
            <h3 className="iv-about-heading">INK VOID Tattoo Studio</h3>
            <p className="iv-about-text">
              2016 yılında İstanbul Kadıköy'ün kalbinde kurulan INK VOID,
              underground dövme kültürünü premium bir deneyimle birleştiren bir
              stüdyodur. Her sanatçımız, kendi tarzının mimarıdır; her
              müşterimiz için özgün, tek ve kalıcı eserler üretiyoruz.
            </p>
            <p className="iv-about-text">
              Steril, güvenli ve profesyonel ortamımızda realism'den
              minimalizme, traditional'dan cover-up'a geniş bir yelpazeyi
              kapsıyoruz. Dövme, sadece bir estetik değil; kimliğinizin deriye
              işlenmiş manifestosudur.
            </p>
            <div className="iv-about-features">
              <div className="iv-feature-row">
                <span className="iv-feature-dot" />
                <span>Tek Kullanımlık Steril Malzeme</span>
              </div>
              <div className="iv-feature-row">
                <span className="iv-feature-dot" />
                <span>Özel Tasarım & Danışmanlık</span>
              </div>
              <div className="iv-feature-row">
                <span className="iv-feature-dot" />
                <span>Uluslararası Ödüllü Sanatçılar</span>
              </div>
            </div>
          </div>
          <div className="iv-about-image-wrap">
            <img
              src="/images/dvm/dvm_real.jpg"
              alt="INK VOID Studio"
              className="iv-about-img"
            />
            <div className="iv-about-img-line" />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CONTACT SECTION
            ══════════════════════════════════════════════════════════════════ */}
      <section id="contact" className="iv-contact-section">
        <div className="iv-section-header">
          <span className="iv-section-tag">İLETİŞİM</span>
          <h2 className="iv-section-title">Bize Ulaşın</h2>
          <p className="iv-section-sub">Randevu ve konsültasyon için</p>
        </div>

        <div className="iv-contact-cards">
          <a href={`tel:${businessInfo.phone}`} className="iv-contact-card">
            <div className="iv-contact-icon">
              <Phone size={26} />
            </div>
            <h3>Telefon</h3>
            <span>{businessInfo.phoneFormatted}</span>
          </a>
          <a
            href={`https://wa.me/${businessInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="iv-contact-card"
          >
            <div className="iv-contact-icon iv-wa-icon">
              <MessageCircle size={26} />
            </div>
            <h3>WhatsApp</h3>
            <span>Mesaj Gönder</span>
          </a>
          <a
            href={`https://www.instagram.com/${businessInfo.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="iv-contact-card"
          >
            <div className="iv-contact-icon iv-ig-icon">
              <AiFillInstagram size={26} />
            </div>
            <h3>Instagram</h3>
            <span>@{businessInfo.instagram}</span>
          </a>
        </div>

        <div className="iv-contact-info-row">
          <div className="iv-info-block">
            <MapPin size={22} className="iv-info-icon" />
            <div>
              <h4>Adres</h4>
              <p>{businessInfo.address}</p>
            </div>
          </div>
          <div className="iv-info-block">
            <Calendar size={22} className="iv-info-icon" />
            <div>
              <h4>Çalışma Saatleri</h4>
              <p>Salı – Cumartesi: 11:00 – 21:00</p>
              <p>Pazar: 13:00 – 19:00</p>
              <p className="iv-closed-day">Pazartesi: Kapalı</p>
            </div>
          </div>
        </div>

        <div className="iv-map-wrap">
          <iframe
            src={businessInfo.mapEmbed}
            width="100%"
            height="100%"
            style={{
              border: 0,
              filter: "grayscale(100%) invert(90%) contrast(1.1)",
            }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="INK VOID Konum"
          />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                CALL TO ACTION — Full Width
            ══════════════════════════════════════════════════════════════════ */}
      <section className="iv-cta-section">
        <div className="iv-cta-inner">
          <span className="iv-cta-tag">HAZIR MISIN?</span>
          <h2 className="iv-cta-title">
            KENDİNİ
            <br />
            İFADE ET
          </h2>
          <p className="iv-cta-sub">Hikayeni deriye işlemenin zamanı geldi.</p>
          <button className="iv-cta-btn" onClick={handleBookAppointment}>
            <Calendar size={18} />
            RANDEVU AL
          </button>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
                FOOTER
            ══════════════════════════════════════════════════════════════════ */}
      <footer className="iv-footer">
        <div className="iv-footer-inner">
          <div className="iv-footer-brand">
            <span className="iv-footer-logo-text">INK VOID</span>
            <p className="iv-footer-slogan">{businessInfo.slogan}</p>
          </div>
          <div className="iv-footer-copy">
            <p>© 2026 INK VOID Tattoo Studio. Tüm hakları saklıdır.</p>
            <p className="iv-footer-powered">
              Powered by{" "}
              <a
                href="https://www.vireliosoft.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vireliosoft
              </a>{" "}
              /{" "}
              <a
                href="https://www.randevupp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                RandevUpp
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════════
                SCROLL TO TOP
            ══════════════════════════════════════════════════════════════════ */}
      {showScrollTop && (
        <button
          className="iv-scroll-top"
          onClick={scrollToTop}
          aria-label="Yukarı Çık"
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 60 60"
            className="iv-progress-ring"
          >
            <circle
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
            />
            <circle
              ref={circleRef}
              stroke="#cc0000"
              strokeWidth="4"
              fill="transparent"
              r="26"
              cx="30"
              cy="30"
              style={{
                strokeDasharray: `${2 * Math.PI * 26}`,
                strokeDashoffset: `${2 * Math.PI * 26}`,
              }}
            />
          </svg>
          <ChevronUp size={20} className="iv-scroll-icon" />
        </button>
      )}

      {/* ══════════════════════════════════════════════════════════════════
                MOBILE BOTTOM BAR
            ══════════════════════════════════════════════════════════════════ */}
      <div className="iv-bottom-bar">
        <div className="iv-bottom-pill">
          <button
            className="iv-bottom-btn install-btn"
            onClick={handleInstallClick}
          >
            <Download size={18} />
            <span>İndir</span>
          </button>
          <div className="iv-bottom-divider" />
          <button
            className="iv-bottom-btn appt-btn"
            onClick={handleBookAppointment}
          >
            <Calendar size={18} />
            <span>Randevu</span>
          </button>
          <div className="iv-bottom-divider" />
          <button className="iv-bottom-btn wa-btn" onClick={handleWhatsapp}>
            <MessageCircle size={18} />
            <span>WhatsApp</span>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
                INSTALL MODAL
            ══════════════════════════════════════════════════════════════════ */}
      {installModalVisible && (
        <div
          className="iv-modal-overlay"
          onClick={() => setInstallModalVisible(false)}
        >
          <div
            className="iv-install-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="iv-modal-handle" />
            <div className="iv-modal-header">
              <h5>Uygulamayı Yükle</h5>
              <button
                className="iv-modal-close"
                onClick={() => setInstallModalVisible(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="iv-modal-body">
              <div className="iv-modal-step">
                <div className="iv-modal-step-icon">
                  <Smartphone size={22} />
                </div>
                <div>
                  <strong>iPhone / Safari</strong>
                  <p>Paylaş butonuna dokun → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
              <div className="iv-modal-step">
                <div className="iv-modal-step-icon">
                  <Download size={22} />
                </div>
                <div>
                  <strong>Android / Chrome</strong>
                  <p>Menü (⋮) → &quot;Ana Ekrana Ekle&quot; seç</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
